import { Box, Divider, Grid, IconButton, Link, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { AddCircleOutline, AssignmentOutlined, BusinessOutlined, CalendarMonthOutlined, Check, Close, ComputerOutlined, DesignServicesOutlined, HelpOutlineOutlined, InsertDriveFileOutlined, KeyboardArrowLeft, KeyboardArrowRight, Phone, RateReviewOutlined, ScheduleOutlined, SupportAgentOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import { NOTIFICATION_TYPE, showNotificationBar } from "../../../actions/error-pages/NotificationBarActions";
import { getSiteContent, getSiteHoursReport, getSiteSubmissions, getSiteSubscription } from '../../../actions/internal/SiteActions';
import { getSiteById, getSiteContentById, getSiteSubmissionsById, getSiteSubscriptionById } from '../../../selectors/SiteSelectors';
import Colors from "../../../constants/Colors";
import { formatDateTimeToFancyString, formatDateToFancyString, getMonthName } from "../../../utilities/DateUtilities";
import { withRouter } from 'react-router';
import { observer } from 'mobx-react-lite';
import StyledSubHeader from '../../common/StyledSubHeader';
import StyledProgressCircle from '../../common/StyledProgressCircle';
import { ContainedButton, TextButton, TooltipIconButton } from '../../common/StyledButtons';
import { CARD_CAPTION_STYLE, CARD_HEADER_STYLE } from '../../../constants/Styles';
import { getUserId } from '../../../selectors/UserSelectors';
import StyledChip from '../../common/StyledChip';
import StyledPage from '../../common/StyledPage';
import StyledSection from '../../common/StyledSection';
import StyledStatusIcon from '../../common/StyledStatusIcon';
import { GENERATION_STATUS, SUBMISSION_TYPES_HUMAN_MAP } from '../../../constants/Statuses';
import ViewRequestModal from '../modals/ViewRequestModal';
import AddRequestModal from '../modals/AddRequestModal';

const ViewSiteContentPage = ({ match, history }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [ isLoading, setIsLoading ] = useState(true);

    const siteId = match.params.site_id
    const content = getSiteContentById(siteId);

    useEffect(() => {
        loadData().then();
    }, [])

    const loadData = async () => {
        try {
            const userId = getUserId();
            await getSiteContent(userId, siteId)
        } catch (ex) {
            console.error(ex);
        } finally {
            setIsLoading(false);
        }
    }

    const renderLoadingContent = () => (
        <StyledProgressCircle />
    )

    const renderArticleName = (article) => {
        if(!article) return 'N/A';
        const title = article.content.topic ? article.content.topic : 'Untitled Article';
        return <span>{title}</span>
    }

    const renderRequestsTooltip = () => (
        <Tooltip
            title={
            <>
                This section shows all active requests open for your business.
                Requests are a way to submit necessary changes or ask for help with problems you might be facing.
            </>
            }
            arrow
            placement="top"
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>Requests</span>
            <HelpOutlineOutlined
                fontSize="small"
                sx={{ marginLeft: '4px', cursor: 'pointer' }}
            />
            </div>
        </Tooltip>
    )

    const renderLibraryTooltip = () => (
        <Tooltip
            title={
            <>
                This section shows all active requests open for your business.
                Requests are a way to submit necessary changes or ask for help with problems you might be facing.
            </>
            }
            arrow
            placement="top"
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
            <span>Library</span>
            <HelpOutlineOutlined
                fontSize="small"
                sx={{ marginLeft: '4px', cursor: 'pointer' }}
            />
            </div>
        </Tooltip>
    )

    const renderOpenNewButton = () => (
        <TextButton
          onClick={() => history.push(`/dashboard/site/${siteId}/content/create`)}
          sx={{ display: 'flex', alignItems: 'center', padding: '4px' }}
        >
          <AddCircleOutline fontSize="small" sx={{ marginRight: '4px' }} />
          Create New Content
        </TextButton>
    );

    const buildArticleIcon = (content) => {
        if (!content || !content.metadata || !content.metadata.published_timestamp) {
          return <StyledStatusIcon status={content.generator_status} />;
        }
        const timestamp = content.metadata.published_timestamp;
      
        const publishDate = new Date(timestamp);
        const now = new Date();
      
        const isFuture = publishDate.getTime() > now.getTime();
      
        return isFuture ? <Tooltip title={"Scheduled: This article will be published at a future time."}><CalendarMonthOutlined style={{ color: Colors.orangeColor,cursor: 'pointer', fontSize: '28px' }}/></Tooltip> : <StyledStatusIcon status={content.generator_status} />;
      }

      const renderLibrarySection = () => (
        <StyledSection
            title={<>{renderLibraryTooltip()}</>}
            rightAdornment={renderOpenNewButton()}
        >
            {isLoading ? 
                <StyledProgressCircle />
                :
                <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List style={{ flex: 1 }}>
                        {content && content.map((c, index) => (
                            <ListItem
                            dense
                            key={c.content_id}
                            sx={{
                                backgroundColor: '#fff',
                                marginBottom: '5px',
                                paddingRight: '55px',
                                borderBottom: index !== content.length - 1 ? '1px solid #e0e0e0' : 'none',
                            }}
                            >
                            <ListItemAvatar>
                                {buildArticleIcon(c)}
                            </ListItemAvatar>

                            <ListItemText
                                primary={renderArticleName(c)}
                                secondary={`Written on ${formatDateToFancyString(c.metadata.generation_start_timestamp)}`}
                            />
                            {(c.generator_status === GENERATION_STATUS.SUCCESS
                                || c.generator_status === GENERATION_STATUS.PUBLISHED) &&
                                <ListItemSecondaryAction sx={{ marginLeft: "10px" }}>
                                    <TooltipIconButton
                                    text="View Article"
                                    icon={
                                        <InsertDriveFileOutlined
                                            sx={{ color: Colors.primaryColor }}
                                            onClick={() => history.push(`/dashboard/site/${siteId}/content/${c.tracking_id}`)}
                                        />
                                    }
                                    />
                                </ListItemSecondaryAction>
                            }
                            
                            </ListItem>
                        ))}
                        
                        {(!content || content.length === 0) && <p>No content created yet.</p>}
                    </List>
                </Paper>
            }
        </StyledSection>
    )

    const renderRequestsSection = () => (
        <StyledSection
            title={<>{renderRequestsTooltip()}</>}
            rightAdornment={renderOpenNewButton()}
        >
            {isLoading ? 
                <StyledProgressCircle />
                :
                <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List style={{ flex: 1 }}>
                        {content && content.map((c, index) => (
                            <ListItem
                            dense
                            key={c.content_id}
                            sx={{
                                backgroundColor: '#fff',
                                marginBottom: '5px',
                                paddingRight: '55px',
                                borderBottom: index !== content.length - 1 ? '1px solid #e0e0e0' : 'none',
                            }}
                            >
                            <ListItemAvatar>
                                {buildArticleIcon(c)}
                            </ListItemAvatar>

                            <ListItemText
                                primary={renderArticleName(c)}
                                secondary={`Written on ${formatDateToFancyString(c.metadata.generation_start_timestamp)}`}
                            />
                            {(c.generator_status === GENERATION_STATUS.SUCCESS
                                || c.generator_status === GENERATION_STATUS.PUBLISHED) &&
                                <ListItemSecondaryAction sx={{ marginLeft: "10px" }}>
                                    <TooltipIconButton
                                    text="View Article"
                                    icon={
                                        <InsertDriveFileOutlined
                                            sx={{ color: Colors.primaryColor }}
                                            onClick={() => history.push(`/dashboard/site/${siteId}/content/${c.tracking_id}`)}
                                        />
                                    }
                                    />
                                </ListItemSecondaryAction>
                            }
                            
                            </ListItem>
                        ))}
                        
                        {(!content || content.length === 0) && <p>No content created yet.</p>}
                    </List>
                </Paper>
            }
        </StyledSection>
    )

    /* const renderCompletedRequestsSection = () => {
        let totalHours = "0.00";
        let cycleEndDate = "-";
        let completedWithTime = 0;
        let trackedRequests = [];
    
        if (hoursReport) {
            if (hoursReport.time_utilized && hoursReport.time_utilized.total_time_hours) {
                totalHours = hoursReport.time_utilized.total_time_hours;
            }
    
            if (hoursReport.time_period && hoursReport.time_period.end) {
                const endDate = new Date(hoursReport.time_period.end);
                cycleEndDate = endDate.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                });
            }
    
            if (Array.isArray(hoursReport.tracked_completed_requests)) {
                trackedRequests = hoursReport.tracked_completed_requests;
                completedWithTime = trackedRequests.filter((r) => {
                    return r.submission_time_utilized &&
                        r.submission_time_utilized.total_time_seconds > 0;
                }).length;
            }
        }
    
        return (
            <StyledSection title="Support Completed">
                {isLoadingDate ? (
                    <StyledProgressCircle />
                ) : (
                    <>
                        <Grid container spacing={2} sx={{ marginBottom: '12px' }}>
                            {[{
                                label: 'Total Hours Completed',
                                value: `${totalHours} hrs`
                            }, {
                                label: 'Cycle End Date',
                                value: cycleEndDate
                            }, {
                                label: 'Requests Tracked',
                                value: completedWithTime
                            }].map((item, idx) => (
                                <Grid item xs={12} sm={4} key={idx}>
                                    <Box
                                        sx={{
                                            padding: '12px',
                                            minHeight: isMobile ? 'auto' : '100px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <h4 style={{ fontSize: '14px', marginBottom: '4px', marginTop: 0 }}>{item.label}</h4>
                                        <h3 style={{ fontSize: '24px', margin: 0 }}>{item.value}</h3>
                                    </Box>
                                    {(isMobile && idx !== 2) && <Divider /> }
                                </Grid>
                            ))}
                        </Grid>
    
                        {trackedRequests && trackedRequests.length > 0 &&
                            <Paper variant="outlined" sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                                <List style={{ flex: 1 }}>
                                    {trackedRequests.length > 0 ? (
                                        trackedRequests.map((submission, index) => (
                                            <ListItem
                                                dense
                                                key={submission.submission_id}
                                                sx={{
                                                    backgroundColor: '#fff',
                                                    marginBottom: '5px',
                                                    borderBottom:
                                                        index !== trackedRequests.length - 1
                                                            ? '1px solid #e0e0e0'
                                                            : 'none',
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <StyledStatusIcon status={submission.submission_status} />
                                                </ListItemAvatar>

                                                <ListItemText
                                                    primary={renderSubmissionTitle(submission)}
                                                    secondary={`Closed on ${formatDateToFancyString(submission.submission_closed_epoch)}`}
                                                />

                                                <ListItemSecondaryAction>
                                                    <TooltipIconButton
                                                        text="View Details"
                                                        icon={
                                                            <AssignmentOutlined
                                                                sx={{ color: Colors.primaryColor }}
                                                                onClick={() =>
                                                                    setRequestInView({
                                                                        ...submission,
                                                                        submission_hours: submission.submission_time_entries,
                                                                    })
                                                                }
                                                            />
                                                        }
                                                    />
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                        ))
                                    ) : (
                                        <p>No completed requests.</p>
                                    )}
                                </List>
                            </Paper>
                        }
                        
                    </>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', marginTop: '32px', position: 'relative', zIndex: 15}}>
                    <ContainedButton
                        sx={{ padding: '8px 16px 8px 16px' }}
                        onClick={handleExploreCompletedSupport}
                    >View More</ContainedButton>
                </Box>
            </StyledSection>
        );
    }; */
    
    
    
    
    

    return (
        <>
            <StyledPage>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <StyledSubHeader title="Content" />
                    </Grid>

                    <Grid item xs={12}>
                    {isLoading ? (
                        renderLoadingContent()
                    ) : (
                        <>
                            {renderLibrarySection()}
                        </>
                    )}
                    </Grid>
                </Grid>
            </StyledPage>
      </>
    )


}

export default observer(withRouter(ViewSiteContentPage));
